import { useEffect } from "react";
import { z } from "zod";
import { SelectionDropdown } from "../../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { Form } from "../../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../../components/Form/FormContext.ts";
import { PanelLayout } from "../../../../../components/Layout/PanelLayout.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../../components/Panel/Settings/ActionsBar.tsx";
import { PromptSection } from "../../../../../components/Panel/Settings/PromptSection.tsx";
import { GENERATION_RATIOS } from "../../../../../constants.ts";
import { GENERATION_RATIO_DISPLAY } from "../../../../../display.ts";
import { useBoardUuid } from "../../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import { scrollToEditionHistoryStore } from "../../../../../stores/scrollToEditionHistoryStore.ts";
import { getErrorsFiltered } from "../../../../../utils/getErrorsFiltered.ts";
import { useExpandImage } from "./hooks/useExpandImage.ts";
import { useExpandSettings } from "./hooks/useExpandSettings.ts";

export const ExpandSettings = () => {
  const { mutate: expandMutate, isLoading: isGenerationLoading } =
    useExpandImage({
      onSuccess: () => {
        scrollToEditionHistoryStore.triggerScrollToEditionHistory();
      },
    });

  return (
    <Form
      schema={zExpandSettings}
      initialValues={{
        selectedAssetUuid: "",
        isSelectedImageSucceeded: false,
      }}
      onSubmit={(values) => {
        // FIXME: make the mutation async
        expandMutate({
          assetUuid: values.selectedAssetUuid,
        });
        return Promise.resolve(true);
      }}
    >
      <ExpandSettingsFormContent isGenerationLoading={isGenerationLoading} />
    </Form>
  );
};

const zExpandSettings = z.object({
  selectedAssetUuid: z
    .string({ required_error: "Please upload an image in the canvas" })
    .uuid(),
  isSelectedImageSucceeded: z.boolean().refine((val) => val, {
    message: "Please wait for your image to be generated",
  }),
});
type ExpandSettingsValues = z.input<typeof zExpandSettings>;

export const ExpandSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const { selectedAsset } = useSelectedAsset();
  const boardUuid = useBoardUuid();
  const { settings, setSettings } = useExpandSettings(boardUuid);

  const { setValues, submit, useError } = useForm<ExpandSettingsValues>();
  const missingAssetError = useError((v) => v.selectedAssetUuid);
  let imageNotSucceededError = useError((v) => v.isSelectedImageSucceeded);
  if (missingAssetError) imageNotSucceededError = undefined;

  useEffect(() => {
    setValues({ selectedAssetUuid: selectedAsset?.uuid });
  }, [setValues, selectedAsset?.uuid]);

  useEffect(() => {
    setValues({
      isSelectedImageSucceeded: selectedAsset?.content.status === "SUCCEEDED",
    });
  }, [setValues, selectedAsset?.content.status]);

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Expand"
          errors={getErrorsFiltered([
            missingAssetError,
            imageNotSucceededError,
          ])}
        />
      }
    >
      <PanelSection
        title="Desired ratio"
        rightItem={
          <SelectionDropdown
            value={settings.generation_ratio}
            onChange={(value) =>
              setSettings({
                generation_ratio: value,
              })
            }
            options={GENERATION_RATIOS.map((generationRatio) => ({
              value: generationRatio,
              ...GENERATION_RATIO_DISPLAY[generationRatio],
            }))}
            size="sm"
            align="end"
            buttonClassName="w-[94px]"
          />
        }
      />
      <PromptSection
        prompt={settings.prompt ?? ""}
        setPrompt={(prompt) =>
          setSettings({
            prompt: prompt.isBlank() ? null : prompt,
          })
        }
        sectionTitle="Expand with"
        placeholder="(Optional) Describe what should appear in the expanded area. If left empty, we'll automatically extend the current background elements to fill the space."
        errors={[]}
      />
    </PanelLayout>
  );
};
